// src/features/admin/components/BulkImportSummaryModal.jsx
import PropTypes from "prop-types";
import { X, CheckCircle2, AlertTriangle } from "lucide-react";

export default function BulkImportSummaryModal({ open, onClose, summary }) {
  if (!open || !summary) return null;

  const { totalRows = 0, createdCount = 0, rowErrors = [] } = summary;

  const normalizeReason = (item) =>
    item?.reason || item?.message || item?.error || "-";
    const formatErrorMeta = (item) => {
    const parts = [];
    if (item?.code) parts.push(item.code);
    if (item?.voucher) parts.push(`Voucher: ${item.voucher}`);
    if (item?.name) parts.push(`Nama: ${item.name}`);
    return parts.length ? ` (${parts.join(" • ")})` : "";
  };

  const totalErrors = rowErrors?.length || 0;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-3xl mx-4 rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Ringkasan Import Pengguna
            </h3>
            <p className="text-sm text-gray-500">
              Hasil proses bulk import dari file yang baru saja diunggah.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Total Baris
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {totalRows}
              </p>
            </div>

            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
              <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">
                Berhasil Dibuat
              </p>
              <p className="mt-1 text-2xl font-semibold text-emerald-700">
                {createdCount}
              </p>
            </div>

            <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3">
              <p className="text-xs font-medium text-red-600 uppercase tracking-wide">
                Error
              </p>
              <p className="mt-1 text-2xl font-semibold text-red-700">
                {totalErrors}
              </p>
            </div>
          </div>

          {/* Errors */}
          <section className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-red-50 text-red-600">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900">
                Error (baris gagal diproses)
              </h4>
              <span className="ml-1 inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                {totalErrors}
              </span>
            </div>

            {totalErrors === 0 ? (
              <p className="text-xs text-gray-500">
                Tidak ada baris yang gagal.
              </p>
            ) : (
              <div className="rounded-lg border border-red-100 bg-red-50/60 px-3 py-2 space-y-1.5">
                {rowErrors.map((item, idx) => (
                  <div
                    key={`${item.rowNumber || idx}-err`}
                    className="text-xs text-red-800"
                  >
                    <span className="font-semibold">
                      Baris {item.rowNumber ?? "?"}
                    </span>
                    {item.email && ` — ${item.email}`}
                    {": "}
                     {normalizeReason(item)}
                    {formatErrorMeta(item)}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Info sukses */}
          {createdCount > 0 && (
            <section>
              <div className="flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2.5 text-xs text-emerald-800">
                <CheckCircle2 className="w-4 h-4 mt-0.5" />
                <p>
                  Data pengguna yang berhasil dibuat sudah langsung masuk ke tabel
                  di halaman ini. Anda dapat melihat{" "}
                  <span className="font-semibold">Password Awal</span> mereka di
                  kolom yang tersedia.
                </p>
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Mengerti
          </button>
        </div>
      </div>
    </div>
  );
}

BulkImportSummaryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  summary: PropTypes.shape({
    totalRows: PropTypes.number,
    createdCount: PropTypes.number,
    rowErrors: PropTypes.arrayOf(
      PropTypes.shape({
        rowNumber: PropTypes.number,
        email: PropTypes.string,
        name: PropTypes.string,
        voucher: PropTypes.string,  
        code: PropTypes.string,    
        reason: PropTypes.string,
        message: PropTypes.string,
        error: PropTypes.string,

      })
    ),
  }),
};
